import {type ReactNode, useState} from 'react'
import {
  Image,
  type ImageResizeMode,
  type StyleProp,
  StyleSheet,
  View,
  type ViewStyle,
} from 'react-native'

/**
 * A remote image drawn over its own placeholder.
 *
 * The placeholder is always rendered underneath; the image sits on top,
 * absolutely positioned, and is kept invisible until it has actually loaded.
 * So a slow, blocked, or failed load never shows a blank area or a half-decoded
 * image - the placeholder just stays visible - and there is no error state to
 * track. Trend and article images come straight from publishers with no CDN
 * of ours in between, so "the site is unreachable for this viewer" is a normal
 * condition, not an edge case.
 *
 * `style` is the container geometry (size / aspect ratio / radius). The
 * placeholder should fill it (e.g. width/height 100%).
 */
export function ImageWithPlaceholder({
  uri,
  placeholder,
  style,
  resizeMode = 'cover',
  testID,
}: {
  uri: string
  placeholder: ReactNode
  style?: StyleProp<ViewStyle>
  resizeMode?: ImageResizeMode
  testID?: string
}) {
  // Keyed by URL so a refreshed image starts hidden again until it loads.
  const [loadedUri, setLoadedUri] = useState<string | null>(null)
  const loaded = loadedUri === uri
  return (
    <View style={[{overflow: 'hidden'}, style]}>
      <View style={StyleSheet.absoluteFill}>{placeholder}</View>
      <Image
        accessibilityIgnoresInvertColors
        testID={testID}
        source={{uri}}
        onLoad={() => setLoadedUri(uri)}
        resizeMode={resizeMode}
        style={[StyleSheet.absoluteFill, {opacity: loaded ? 1 : 0}]}
      />
    </View>
  )
}
